import { Check, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/utils/cn'

interface CompleteData {
  // Básico
  profileType: string
  name: string
  email: string
  cpf: string
  username: string
  phone?: string
  city: string
  state: string
  profession: string
  howKnew: string
  aiFamiliarity: string
  termsAccepted: boolean

  // Específicos
  propertySizeHa?: string
  biome?: string
  soilType?: string
  productionSystem?: string
  gaasAssociated?: string
  activities?: string[]
  practices?: string[]
  challenges?: string[]
  mainObjective?: string
  professionalFormation?: string
  workArea?: string
  workRegion?: string
  workType?: string
  regenerativeExperience?: string
  experienceYears?: string
  howUseRegenerai?: string
  institution?: string
  workField?: string
  mainInterest?: string

  // Preferências
  aiUsage?: string
  preferredTopics?: string[]
  dataSharingConsent?: string
  observations?: string
}

interface ReviewStepProps {
  data: CompleteData
  onConfirm: () => void
  isLoading?: boolean
}

const profileTypeLabels = {
  producer: 'Produtor(a) Rural',
  technician: 'Técnico(a) / Consultor(a)',
  researcher: 'Pesquisador(a)',
  student: 'Estudante',
  institution: 'Representante de Instituição',
  other: 'Outro'
}

const howKnewLabels = {
  gaas: 'GAAS',
  indication: 'Indicação',
  event: 'Evento',
  social_media: 'Redes sociais',
  website: 'Site',
  other: 'Outro'
}

const aiFamiliarityLabels = {
  never: 'Nunca usei',
  occasionally: 'Uso ocasionalmente',
  frequently: 'Uso com frequência'
}

export function ReviewStep({ data, onConfirm, isLoading }: ReviewStepProps) {
  const [showCPF, setShowCPF] = useState(false)

  const renderBasicInfo = () => (
    <div className="space-y-4">
      <h4 className="font-semibold text-brand-text flex items-center gap-2">
        <Check size={18} className="text-brand-primary" />
        Informações Básicas
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
        <div>
          <span className="text-sm text-brand-text-muted">Nome:</span>
          <p className="text-brand-text">{data.name}</p>
        </div>

        <div>
          <span className="text-sm text-brand-text-muted">Email:</span>
          <p className="text-brand-text">{data.email}</p>
        </div>

        <div>
          <span className="text-sm text-brand-text-muted">CPF:</span>
          <div className="flex items-center gap-2">
            <p className="text-brand-text">
              {showCPF ? data.cpf : '***.***.***-**'}
            </p>
            <button
              type="button"
              onClick={() => setShowCPF(!showCPF)}
              className="text-brand-primary hover:underline"
            >
              {showCPF ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <span className="text-sm text-brand-text-muted">Username:</span>
          <p className="text-brand-text">@{data.username}</p>
        </div>

        {data.phone && (
          <div>
            <span className="text-sm text-brand-text-muted">Telefone:</span>
            <p className="text-brand-text">{data.phone}</p>
          </div>
        )}

        <div>
          <span className="text-sm text-brand-text-muted">Localidade:</span>
          <p className="text-brand-text">{data.city} - {data.state}</p>
        </div>

        <div>
          <span className="text-sm text-brand-text-muted">Profissão:</span>
          <p className="text-brand-text">{data.profession}</p>
        </div>

        <div>
          <span className="text-sm text-brand-text-muted">Como conheceu:</span>
          <p className="text-brand-text">{howKnewLabels[data.howKnew as keyof typeof howKnewLabels]}</p>
        </div>

        <div>
          <span className="text-sm text-brand-text-muted">Familiaridade com IA:</span>
          <p className="text-brand-text">{aiFamiliarityLabels[data.aiFamiliarity as keyof typeof aiFamiliarityLabels]}</p>
        </div>
      </div>
    </div>
  )

  const renderSpecificInfo = () => {
    if (data.profileType === 'producer') {
      return (
        <div className="space-y-4">
          <h4 className="font-semibold text-brand-text flex items-center gap-2">
            <Check size={18} className="text-brand-primary" />
            Informações da Propriedade
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
            {data.propertySizeHa && (
              <div>
                <span className="text-sm text-brand-text-muted">Área:</span>
                <p className="text-brand-text">{data.propertySizeHa} hectares</p>
              </div>
            )}

            {data.biome && (
              <div>
                <span className="text-sm text-brand-text-muted">Bioma:</span>
                <p className="text-brand-text">{data.biome}</p>
              </div>
            )}

            {data.soilType && (
              <div>
                <span className="text-sm text-brand-text-muted">Tipo de solo:</span>
                <p className="text-brand-text">{data.soilType}</p>
              </div>
            )}

            {data.productionSystem && (
              <div>
                <span className="text-sm text-brand-text-muted">Sistema de produção:</span>
                <p className="text-brand-text">{data.productionSystem}</p>
              </div>
            )}

            {data.gaasAssociated && (
              <div>
                <span className="text-sm text-brand-text-muted">Associado GAAS:</span>
                <p className="text-brand-text">
                  {data.gaasAssociated === 'yes' ? 'Sim' :
                    data.gaasAssociated === 'no' ? 'Não' : 'Ainda não sei'}
                </p>
              </div>
            )}

            {data.mainObjective && (
              <div>
                <span className="text-sm text-brand-text-muted">Objetivo principal:</span>
                <p className="text-brand-text">{data.mainObjective}</p>
              </div>
            )}

            {data.activities && data.activities.length > 0 && (
              <div className="md:col-span-2">
                <span className="text-sm text-brand-text-muted">Atividades:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {data.activities.map(activity => (
                    <span key={activity} className="px-2 py-1 bg-brand-primary/10 text-brand-primary rounded-lg text-sm">
                      {activity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.practices && data.practices.length > 0 && (
              <div className="md:col-span-2">
                <span className="text-sm text-brand-text-muted">Práticas de manejo:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {data.practices.map(practice => (
                    <span key={practice} className="px-2 py-1 bg-brand-accent/10 text-brand-accent rounded-lg text-sm">
                      {practice}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }

    if (data.profileType === 'technician') {
      return (
        <div className="space-y-4">
          <h4 className="font-semibold text-brand-text flex items-center gap-2">
            <Check size={18} className="text-brand-primary" />
            Informações Profissionais
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
            {data.professionalFormation && (
              <div>
                <span className="text-sm text-brand-text-muted">Formação:</span>
                <p className="text-brand-text">{data.professionalFormation}</p>
              </div>
            )}

            {data.workArea && (
              <div>
                <span className="text-sm text-brand-text-muted">Área de atuação:</span>
                <p className="text-brand-text">{data.workArea}</p>
              </div>
            )}

            {data.workRegion && (
              <div>
                <span className="text-sm text-brand-text-muted">Região:</span>
                <p className="text-brand-text">{data.workRegion}</p>
              </div>
            )}

            {data.workType && (
              <div>
                <span className="text-sm text-brand-text-muted">Vínculo:</span>
                <p className="text-brand-text">{data.workType}</p>
              </div>
            )}

            {data.regenerativeExperience && (
              <div>
                <span className="text-sm text-brand-text-muted">Experiência regenerativa:</span>
                <p className="text-brand-text">
                  {data.regenerativeExperience === 'yes' ? 'Sim' :
                    data.regenerativeExperience === 'no' ? 'Não' : 'Em transição'}
                </p>
              </div>
            )}

            {data.experienceYears && (
              <div>
                <span className="text-sm text-brand-text-muted">Tempo de experiência:</span>
                <p className="text-brand-text">{data.experienceYears}</p>
              </div>
            )}

            {data.howUseRegenerai && (
              <div className="md:col-span-2">
                <span className="text-sm text-brand-text-muted">Como pretende usar:</span>
                <p className="text-brand-text">{data.howUseRegenerai}</p>
              </div>
            )}
          </div>
        </div>
      )
    }

    if (data.profileType === 'researcher' || data.profileType === 'student' || data.profileType === 'institution') {
      return (
        <div className="space-y-4">
          <h4 className="font-semibold text-brand-text flex items-center gap-2">
            <Check size={18} className="text-brand-primary" />
            Informações Acadêmicas/Institucionais
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
            {data.institution && (
              <div>
                <span className="text-sm text-brand-text-muted">Instituição:</span>
                <p className="text-brand-text">{data.institution}</p>
              </div>
            )}

            {data.professionalFormation && (
              <div>
                <span className="text-sm text-brand-text-muted">Formação:</span>
                <p className="text-brand-text">{data.professionalFormation}</p>
              </div>
            )}

            {data.workField && (
              <div>
                <span className="text-sm text-brand-text-muted">Tipo de atuação:</span>
                <p className="text-brand-text">{data.workField}</p>
              </div>
            )}

            {data.mainInterest && (
              <div>
                <span className="text-sm text-brand-text-muted">Interesse principal:</span>
                <p className="text-brand-text">{data.mainInterest}</p>
              </div>
            )}

            {data.preferredTopics && data.preferredTopics.length > 0 && (
              <div className="md:col-span-2">
                <span className="text-sm text-brand-text-muted">Temas de interesse:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {data.preferredTopics.map(topic => (
                    <span key={topic} className="px-2 py-1 bg-brand-accent/10 text-brand-accent rounded-lg text-sm">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }

    return null
  }

  const renderPreferences = () => (
    <div className="space-y-4">
      <h4 className="font-semibold text-brand-text flex items-center gap-2">
        <Check size={18} className="text-brand-primary" />
        Preferências de Uso
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
        {data.aiUsage && (
          <div>
            <span className="text-sm text-brand-text-muted">Uso de IA:</span>
            <p className="text-brand-text">
              {data.aiUsage === 'never' ? 'Nunca usei' :
                data.aiUsage === 'sometimes' ? 'Uso às vezes' : 'Uso com frequência'}
            </p>
          </div>
        )}

        {data.dataSharingConsent && (
          <div>
            <span className="text-sm text-brand-text-muted">Compartilhamento de dados:</span>
            <p className="text-brand-text">
              {data.dataSharingConsent === 'yes' ? 'Sim' :
                data.dataSharingConsent === 'no' ? 'Não' : 'Quero entender melhor'}
            </p>
          </div>
        )}

        {data.observations && (
          <div className="md:col-span-2">
            <span className="text-sm text-brand-text-muted">Observações:</span>
            <p className="text-brand-text whitespace-pre-wrap">{data.observations}</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-8">

      <div className="space-y-6">
        {/* Perfil selecionado */}
        <div className="bg-brand-primary/5 rounded-xl p-4 border border-brand-primary/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold">
              {data.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h4 className="font-semibold text-brand-text">{data.name}</h4>
              <p className="text-brand-text-secondary">
                {profileTypeLabels[data.profileType as keyof typeof profileTypeLabels]}
              </p>
            </div>
          </div>
        </div>

        {/* Informações detalhadas */}
        {renderBasicInfo()}
        {renderSpecificInfo()}
        {renderPreferences()}

        {/* Termos aceitos */}
        <div className="bg-green-50 dark:bg-green-950/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <Check size={20} className="text-green-600" />
            <div>
              <h4 className="font-semibold text-green-700 dark:text-green-300">
                Termos aceitos
              </h4>
              <p className="text-green-600 dark:text-green-400 text-sm">
                Você aceitou os termos de uso e política de dados
              </p>
            </div>
          </div>
        </div>

        {/* Aviso importante */}
        <div className="bg-brand-accent/10 rounded-xl p-4 border border-brand-accent/30">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-brand-accent mt-0.5" />
            <div>
              <h4 className="font-semibold text-brand-text mb-2">
                Importante
              </h4>
              <ul className="text-brand-text-secondary text-sm space-y-1">
                <li>• Seu email e CPF são únicos e não podem ser alterados após o cadastro</li>
                <li>• Você poderá completar ou editar outras informações a qualquer momento</li>
                <li>• Seus dados serão usados para personalizar as respostas do RegenerAI</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Botão de confirmação */}
      <div className="flex justify-center pt-6">
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className={cn(
            "btn-primary flex items-center gap-2 px-8 py-3 text-lg",
            isLoading && "opacity-50 cursor-not-allowed"
          )}
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Criando cadastro...
            </>
          ) : (
            <>
              <Check size={20} />
              Confirmar e Criar Cadastro
            </>
          )}
        </button>
      </div>
    </div>
  )
}
